import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { 
  FiPlus, 
  FiSearch, 
  FiEdit2, 
  FiTrash2, 
  FiX, 
  FiMail, 
  FiPhone, 
  FiUser, 
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw
} from "react-icons/fi";
import { HiOutlineStatusOnline } from "react-icons/hi";
import { BsThreeDotsVertical } from "react-icons/bs";

const LeadDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(null);
  const ref = useRef();
  
  const [form, setForm] = useState({
    name: "",
    email: "",
    number: "",
    remark: "",
  });

  const limit = 5;

  // Status colors mapping
  const statusColors = {
    New: "bg-blue-100 text-blue-800 border-blue-200",
    Contacted: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Interested: "bg-purple-100 text-purple-800 border-purple-200",
    Converted: "bg-green-100 text-green-800 border-green-200",
    Closed: "bg-gray-100 text-gray-800 border-gray-200"
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/lead/getAll?page=${page}&limit=${limit}&search=${search}`
      );
      setLeads(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchLeads();
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [page, search]);

  const openAddModal = () => {
    setEditLead(null);
    setForm({
      name: "",
      email: "",
      number: "",
      remark: "",
    });
    setShowModal(true);
  };

  const openEditModal = (lead) => {
    setEditLead(lead);
    setForm(lead);
    setShowModal(true);
  };

  const submitLead = async () => {
    setLoading(true);
    try {
      if (editLead) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/lead/get-by-id/${editLead._id}`,
          form
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/lead/create`,
          form
        );
      }
      setShowModal(false);
      fetchLeads();
    } catch (error) {
      console.error("Error submitting lead:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/lead/delete/${id}`);
      fetchLeads();
    } catch (error) {
      console.error("Error deleting lead:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    setLoading(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/lead/status/${id}`,
        { status }
      );
      setShowStatusDropdown(null);
      fetchLeads();
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <span className="bg-indigo-600 w-2 h-8 rounded-full"></span>
              Lead Management Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Manage and track your leads efficiently</p>
          </div>
          
          <button
            onClick={openAddModal}
            className="group relative bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 overflow-hidden"
          >
            <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left opacity-20"></span>
            <FiPlus className="w-5 h-5" />
            Add New Lead
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search leads by name, email, or phone..."
            className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm bg-white/70 backdrop-blur-sm"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {['New', 'Contacted', 'Interested', 'Converted', 'Closed'].map((status) => (
            <div key={status} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">{status}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
                  {leads.filter(l => l.status === status).length}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Table Section */}
        <div className="bg-white ">
          {loading && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            </div>
          )}
          
          <div className="">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lead</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remark</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <FiUser className="w-12 h-12 text-gray-300 mb-3" />
                        <p>No leads found</p>
                        <button 
                          onClick={openAddModal}
                          className="mt-2 text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          Add your first lead
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm shadow-md">
                            {lead.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">{lead.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <FiMail className="w-4 h-4 mr-2 text-gray-400" />
                            {lead.email}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <FiPhone className="w-4 h-4 mr-2 text-gray-400" />
                            {lead.number}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <FiMessageSquare className="w-4 h-4 mr-2 text-gray-400 mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-600 line-clamp-2">{lead.remark || "No remarks"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative">
                          <button
                            onClick={() => setShowStatusDropdown(showStatusDropdown === lead._id ? null : lead._id)}
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${statusColors[lead.status]} transition-all hover:shadow-md`}
                          >
                            <HiOutlineStatusOnline className="w-4 h-4 mr-1.5" />
                            {lead.status}
                          </button>
                          
                          {showStatusDropdown === lead._id && (
                            <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                              {['New', 'Contacted', 'Interested', 'Converted', 'Closed'].map((status) => (
                                <button
                                  key={status}
                                  onClick={() => updateStatus(lead._id, status)}
                                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${status === lead.status ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700'}`}
                                >
                                  {status}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(lead)}
                            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                            title="Edit lead"
                          >
                            <FiEdit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => deleteLead(lead._id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete lead"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            
            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= page - 1 && pageNum <= page + 1)
              ) {
                return (
                  <button
                    key={i}
                    onClick={() => setPage(pageNum)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                      page === pageNum
                        ? "bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md"
                        : "border border-gray-200 hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (
                pageNum === page - 2 ||
                pageNum === page + 2
              ) {
                return <span key={i} className="text-gray-400">...</span>;
              }
              return null;
            })}
            
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  {editLead ? "Edit Lead" : "Add New Lead"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    placeholder="Full Name"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    placeholder="Email Address"
                    type="email"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    placeholder="Phone Number"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={form.number}
                    onChange={(e) => setForm({ ...form, number: e.target.value })}
                  />
                </div>
                
                <div className="relative">
                  <FiMessageSquare className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <textarea
                    placeholder="Add remarks..."
                    rows="3"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    value={form.remark}
                    onChange={(e) => setForm({ ...form, remark: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={submitLead}
                  disabled={!form.name || !form.email || !form.number}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Lead'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDashboard;